/* Three-second rotation with manual controls and a native swipe fallback. */
(() => {
  const cleanups = new WeakMap();
  function init(root = document) {
    root.querySelectorAll('[data-gift-slideshow]').forEach(carousel => {
      if (carousel.dataset.ready) return;
      const track = carousel.querySelector('[data-gift-slides]');
      if (!track) return;
      carousel.dataset.ready = 'true';
      const slides = [...track.children];
      const previous = carousel.querySelector('[data-gift-slide-prev]');
      const next = carousel.querySelector('[data-gift-slide-next]');
      const status = carousel.querySelector('[data-gift-slide-status]');
      const rotation = carousel.querySelector('[data-gift-slide-toggle]');
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const listeners = [];
      let paused = motion.matches;
      let hovered = false;
      let inView = !('IntersectionObserver' in window);
      let timer;
      let observer;
      let index = 0;
      function listen(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        listeners.push(() => target.removeEventListener(type, handler, options));
      }
      function stopTimer() {
        window.clearTimeout(timer);
        timer = undefined;
      }
      function warmNext() {
        const upcoming = slides[(index + 1) % slides.length].querySelector('img');
        if (upcoming) upcoming.loading = 'eager';
      }
      function syncRotation() {
        stopTimer();
        if (!rotation) return;
        const label = paused ? 'Play slideshow' : 'Pause slideshow';
        rotation.setAttribute('aria-label', label);
        rotation.setAttribute('title', label);
        rotation.querySelector('[data-gift-rotation-icon]').setAttribute('d', paused ? 'M8 4l12 8-12 8z' : 'M6 4h4v16H6zM14 4h4v16h-4z');
        const running = !paused && !hovered && inView && !document.hidden && slides.length > 1;
        carousel.dataset.autoPlaying = String(running);
        status.setAttribute('aria-live', running ? 'off' : 'polite');
        if (!running) return;
        warmNext();
        timer = window.setTimeout(() => {
          if (!carousel.isConnected) { destroy(); return; }
          show((index + 1) % slides.length, true);
          syncRotation();
        }, 3000);
      }
      function update() {
        if (!track.clientWidth) return;
        index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        status.textContent = `${index + 1} of ${slides.length}`;
        previous.disabled = index === 0;
        next.disabled = index === slides.length - 1;
        slides.forEach((slide, position) => slide.setAttribute('aria-hidden', String(position !== index)));
        if (carousel.dataset.autoPlaying === 'true') warmNext();
      }
      function show(target, automatic = false) {
        const destination = Math.max(0, Math.min(slides.length - 1, target));
        const image = slides[destination].querySelector('img');
        if (image) image.loading = 'eager';
        track.scrollTo({left:destination * track.clientWidth, behavior:motion.matches || (automatic && destination === 0) ? 'instant' : 'smooth'});
      }
      function manualShow(target) {
        paused = true;
        syncRotation();
        show(target);
      }
      listen(previous, 'click', () => manualShow(index - 1));
      listen(next, 'click', () => manualShow(index + 1));
      listen(track, 'scroll', update, {passive:true});
      listen(track, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const targets = {ArrowLeft:index - 1, ArrowRight:index + 1, Home:0, End:slides.length - 1};
        if (!(event.key in targets)) return;
        event.preventDefault();
        manualShow(targets[event.key]);
      });
      if (rotation && slides.length > 1) {
        rotation.hidden = false;
        carousel.dataset.autoReady = 'true';
        listen(rotation, 'click', () => { paused = !paused; syncRotation(); });
        listen(carousel, 'pointerenter', event => {
          if (event.pointerType === 'touch') return;
          hovered = true;
          syncRotation();
        });
        listen(carousel, 'pointerleave', () => { hovered = false; syncRotation(); });
        listen(carousel, 'focusin', event => {
          // Pointer activation of Pause must retain its intended action. Keyboard
          // focus stops rotation until the visitor explicitly chooses Play.
          if (event.target === rotation && !rotation.matches(':focus-visible')) return;
          paused = true;
          syncRotation();
        });
        listen(track, 'pointerdown', () => { paused = true; syncRotation(); });
        listen(document, 'visibilitychange', syncRotation);
        listen(motion, 'change', () => {
          if (motion.matches) paused = true;
          syncRotation();
        });
        if ('IntersectionObserver' in window) {
          observer = new window.IntersectionObserver(entries => {
            inView = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5);
            syncRotation();
          }, {threshold:0.5});
          observer.observe(track);
        }
      }
      function destroy() {
        stopTimer();
        observer?.disconnect();
        listeners.forEach(remove => remove());
        cleanups.delete(carousel);
        delete carousel.dataset.ready;
        delete carousel.dataset.autoReady;
        delete carousel.dataset.autoPlaying;
      }
      cleanups.set(carousel, destroy);
      carousel.querySelector('[data-gift-slide-controls]').hidden = false;
      update();
      syncRotation();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-gift-slideshow]').forEach(carousel => cleanups.get(carousel)?.());
  });
})();
